import {
    useGetShopifyLocationMappingsQuery,
    useGetShopifyLocationsQuery,
} from "@/redux/features/integrations/shopifyApi";
import { useGetModuleLocationsQuery } from "@/redux/features/locations/locationsApi";
import { useCallback, useMemo } from "react";
import { getSuppliLocationKey, LocationsMapperFormTyping } from "./utils";
import { ArrayElementType } from "@helpers/utils";

export const useAvailableShopifyLocations = (mappings: LocationsMapperFormTyping["mappings"]) => {
    const { data: allShopifyLocations, isFetching } = useGetShopifyLocationsQuery();
    const { data: savedMappings } = useGetShopifyLocationMappingsQuery();

    const getAvailableOptions = useCallback(
        (index: number) => {
            if (!allShopifyLocations) return [];

            const currentId = mappings?.[index]?.shopify_location?.shopify_id;

            const usedIds = new Set<string>();
            // Add form selections
            mappings
                ?.map((m) => m.shopify_location?.shopify_id)
                .filter((id): id is string => !!id)
                .forEach((id) => usedIds.add(id));
            // Add already saved mappings
            savedMappings?.forEach((m) => {
                if (m.shopify_location_id) usedIds.add(m.shopify_location_id);
            });

            // Allow current selection but exclude other used ones
            if (currentId) {
                usedIds.delete(currentId);
            }

            return allShopifyLocations.filter((loc) => !usedIds.has(loc.shopify_id));
        },
        [allShopifyLocations, mappings]
    );

    return {
        getAvailableOptions,
        isLoading: isFetching,
    };
};

export const useAvailableSuppliLocations = (mappings: LocationsMapperFormTyping["mappings"]) => {
    const { data: allSuppliLocations, isFetching } = useGetModuleLocationsQuery("integrations");
    const { data: savedMappings } = useGetShopifyLocationMappingsQuery();

    const getAvailableOptions = useCallback(
        (index: number) => {
            if (!allSuppliLocations) return [];

            const currentKey = getSuppliLocationKey(mappings?.[index]?.suppli_location);
            const usedKeys = new Set<string>();

            // Add form selections
            mappings
                ?.map((m) => getSuppliLocationKey(m.suppli_location))
                .filter((key): key is string => !!key)
                .forEach((key) => usedKeys.add(key));

            // Add already saved mappings
            savedMappings?.forEach((m) => {
                const key = `${m.location.store_id ?? "null"}_${m.location.section_id ?? "null"}`;
                usedKeys.add(key);
            });

            // Allow current selection but exclude other used ones
            if (currentKey) {
                usedKeys.delete(currentKey);
            }

            return allSuppliLocations.filter((loc) => {
                const key = getSuppliLocationKey(loc);
                return key ? !usedKeys.has(key) : true;
            });
        },
        [allSuppliLocations, mappings]
    );

    return {
        getAvailableOptions,
        isLoading: isFetching,
    };
};

export const useHasLocationMappingChanges = (
    currentMappings: LocationsMapperFormTyping["mappings"]
) => {
    const { data: savedMappings } = useGetShopifyLocationMappingsQuery();

    return useMemo(() => {
        if (!savedMappings) return false;

        const currentNormalized = currentMappings
            .filter((m) => m.shopify_location || m.suppli_location)
            .map((m) => ({
                shopify_location_id: m.shopify_location?.shopify_id ?? "",
                ...(m.suppli_location?.store.id && {
                    location: {
                        store_id: m.suppli_location?.store.id,
                        section_id: m.suppli_location?.section?.id,
                    },
                })
            }))
            .sort((a, b) => a.shopify_location_id.localeCompare(b.shopify_location_id));

        const savedNormalized = savedMappings
            .map((m) => ({
                shopify_location_id: m.shopify_location_id,
                location: {
                    store_id: m.location.store_id,
                    section_id: m.location.section_id,
                }
            }))
            .sort((a, b) => a.shopify_location_id.localeCompare(b.shopify_location_id));

        if (currentNormalized.length !== savedNormalized.length) return true;

        return currentNormalized.some((curr, idx) => {
            const saved: ArrayElementType<typeof savedNormalized> = savedNormalized[idx];
            return (
                curr.shopify_location_id !== saved.shopify_location_id ||
                curr.location?.store_id !== saved.location.store_id ||
                curr.location?.section_id !== saved.location.section_id
            );
        });
    }, [currentMappings, savedMappings]);
};
